import React, { useState, useEffect, useRef } from 'react';
import { UserProfile } from '@/types/club-community';
import { useAppTranslations } from '@/hooks/useAppTranslations';

interface MentionAutocompleteProps {
  members: UserProfile[];
  inputValue: string;
  cursorPosition: number;
  onSelect: (username: string, startPos: number, endPos: number) => void;
  className?: string;
}

interface MentionMatch {
  start: number;
  end: number;
  query: string;
}

export function MentionAutocomplete({
  members,
  inputValue,
  cursorPosition,
  onSelect,
  className = ''
}: MentionAutocompleteProps) {
  const { t } = useAppTranslations();
  const [isVisible, setIsVisible] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState<UserProfile[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionMatch, setMentionMatch] = useState<MentionMatch | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Find mention pattern at cursor position
  useEffect(() => {
    const findMentionAtCursor = (): MentionMatch | null => {
      if (!inputValue || cursorPosition < 1) return null;

      // Look backwards from cursor to find @
      let start = cursorPosition - 1;
      while (start >= 0 && inputValue[start] !== '@' && inputValue[start] !== ' ') {
        start--;
      }

      if (start < 0 || inputValue[start] !== '@') return null;

      // Look forwards to find end of mention
      let end = cursorPosition;
      while (end < inputValue.length && /\w/.test(inputValue[end])) {
        end++;
      }

      const query = inputValue.slice(start + 1, end);
      return { start, end, query };
    };

    const match = findMentionAtCursor();
    setMentionMatch(match);

    if (match && match.query.length > 0) {
      const filtered = members.filter(member =>
        member.display_name?.toLowerCase().includes(match.query.toLowerCase())
      );
      setFilteredMembers(filtered);
      setIsVisible(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setIsVisible(false);
      setFilteredMembers([]);
    }
  }, [inputValue, cursorPosition, members]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible || filteredMembers.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredMembers.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : filteredMembers.length - 1
          );
          break;
        case 'Enter':
        case 'Tab':
          if (mentionMatch && filteredMembers[selectedIndex]) {
            e.preventDefault();
            handleSelectMember(filteredMembers[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsVisible(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, filteredMembers, selectedIndex, mentionMatch]);

  const handleSelectMember = (member: UserProfile) => {
    if (!mentionMatch || !member.display_name) return;

    onSelect(member.display_name, mentionMatch.start, mentionMatch.end);
    setIsVisible(false);
  };

  const handleClickMember = (member: UserProfile) => {
    handleSelectMember(member);
  };

  if (!isVisible || filteredMembers.length === 0) {
    return null;
  }

  return (
    <div 
      ref={listRef}
      className={`absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto ${className}`}
      style={{
        bottom: '100%',
        marginBottom: '4px',
        minWidth: '200px'
      }}
    >
      {filteredMembers.map((member, index) => (
        <div
          key={member.id}
          className={`flex items-center space-x-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
            index === selectedIndex ? 'bg-green-50 border-l-2 border-green-500' : ''
          }`}
          onClick={() => handleClickMember(member)}
        >
          <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {member.display_name?.[0]?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900 truncate">
              {member.display_name || t.clubCommunity.unknownUser}
            </div>
            {member.handicap !== null && member.handicap !== undefined && (
              <div className="text-xs text-gray-500">
                {t.clubCommunity.handicap} {member.handicap}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}